import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Cpu, LayoutGrid, List, MapPin, Plus, Server } from 'lucide-react';
import { cn } from '@/lib/utils';
import { sitesApi } from '@/api/client';
import { RoleGate } from '@/components/auth/RoleGate';
import { Role } from '@jeket-cloud/types/auth';
import type { Site } from '@/types';
import { CreateSiteDialog } from './dialogs/CreateSiteDialog';
import { SiteHealthIndicator, SiteStatusBadge } from './shared';

// ---- Site Card ----

function SiteCard({ site }: { site: Site }) {
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(`/sites/${site.id}`)}
      className="bg-surface border border-surface-elevated rounded-xl p-5 cursor-pointer hover:border-gold/30 transition-colors group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-surface-elevated flex items-center justify-center">
            <Server size={18} className="text-gold" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-silver-bright group-hover:text-gold-light transition-colors">
              {site.name}
            </h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <MapPin size={10} className="text-silver-muted" />
              <span className="text-xs text-silver-muted">{site.location}</span>
            </div>
          </div>
        </div>
        <SiteHealthIndicator status={site.status} />
      </div>
      {site.description && (
        <p className="text-xs text-silver-muted mb-3 line-clamp-2">{site.description}</p>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <SiteStatusBadge status={site.status} reason={site.statusReason} />
          <span className="text-xs text-silver-muted">
            {site.machineCount} {site.machineCount === 1 ? 'node' : 'nodes'}
          </span>
        </div>
        <ChevronRight size={14} className="text-silver-muted group-hover:text-gold transition-colors" />
      </div>
    </div>
  );
}

// ---- Site Row ----

function SiteRow({ site }: { site: Site }) {
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(`/sites/${site.id}`)}
      className="flex items-center justify-between py-3 px-4 border-b border-surface-elevated last:border-b-0 hover:bg-surface-elevated/50 cursor-pointer transition-colors"
    >
      <div className="flex items-center gap-3">
        <SiteHealthIndicator status={site.status} />
        <div>
          <span className="text-sm font-medium text-silver-bright">{site.name}</span>
          <span className="text-xs text-silver-muted ml-2">{site.location}</span>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <span className="text-xs text-silver-muted">
          {site.machineCount} {site.machineCount === 1 ? 'node' : 'nodes'}
        </span>
        <SiteStatusBadge status={site.status} reason={site.statusReason} />
      </div>
    </div>
  );
}

// ---- Site List View ----

export default function SitesList() {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [createOpen, setCreateOpen] = useState(false);

  const { data: sites, isLoading, error } = useQuery({
    queryKey: ['sites'],
    queryFn: sitesApi.list,
    refetchInterval: 10_000,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-silver-bright">Sites</h2>
          <p className="text-sm text-silver-muted mt-1">
            Your on-prem sites, machines and JBS installs
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/sites/machines"
            className="flex items-center gap-2 px-4 py-2 text-sm text-silver-muted hover:text-silver-bright border border-surface-elevated rounded-lg transition-colors"
          >
            <Cpu size={16} />
            Nodes
          </Link>
          <div className="flex items-center bg-surface border border-surface-elevated rounded-lg">
            <button
              onClick={() => setViewMode('grid')}
              className={cn(
                'p-2 rounded-l-lg transition-colors',
                viewMode === 'grid'
                  ? 'bg-gold/10 text-gold'
                  : 'text-silver-muted hover:text-silver-bright'
              )}
            >
              <LayoutGrid size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                'p-2 rounded-r-lg transition-colors',
                viewMode === 'list'
                  ? 'bg-gold/10 text-gold'
                  : 'text-silver-muted hover:text-silver-bright'
              )}
            >
              <List size={16} />
            </button>
          </div>
          {/* POST /sites is SiteOperator at the BFF (JSS#970). */}
          <RoleGate role={Role.SiteOperator}>
            <button
              onClick={() => setCreateOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gold text-surface-deep text-sm font-medium rounded-lg hover:bg-gold-light transition-colors"
            >
              <Plus size={16} />
              New Site
            </button>
          </RoleGate>
        </div>
      </div>

      {/* Content */}
      {isLoading && (
        <div className="text-center py-12 text-silver-muted text-sm">Loading sites...</div>
      )}
      {error && !sites && (
        <div className="text-center py-12 text-status-error text-sm">Failed to load sites</div>
      )}
      {sites && sites.length === 0 && (
        <div className="text-center py-16 space-y-3">
          <Server size={40} className="mx-auto text-silver-muted/30" />
          <p className="text-silver-muted">No sites yet</p>
          <RoleGate
            role={Role.SiteOperator}
            fallback={
              <p className="text-xs text-silver-muted" data-testid="create-site-needs-role">
                A Site Operator or Admin of your organization can create one.
              </p>
            }
          >
            <button
              onClick={() => setCreateOpen(true)}
              className="text-gold text-sm hover:text-gold-light transition-colors"
            >
              Create your first site
            </button>
          </RoleGate>
        </div>
      )}

      {sites && sites.length > 0 && viewMode === 'grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sites.map((site) => (
            <SiteCard key={site.id} site={site} />
          ))}
        </div>
      )}

      {sites && sites.length > 0 && viewMode === 'list' && (
        <div className="bg-surface border border-surface-elevated rounded-xl">
          {sites.map((site) => (
            <SiteRow key={site.id} site={site} />
          ))}
        </div>
      )}

      <CreateSiteDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}
